import { EventEditModal } from "@/components/event/EventEditModal.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EventInCalendar } from "@/pages/calendar/components/EventInCalendar.tsx";
import calendarTimeZonePlugin from "@/pages/calendar/calendar-timezone-plugin.ts";
import {
  useEmployeeMe,
  useEmployeeTimeOffCalendar,
} from "@/queries/employees/use-employees.query.ts";
import { useEvents } from "@/queries/use-events.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import {
  ApiEvent,
  ApiEventEdit,
  ApiEventFilter,
  CalendarEventType,
  getMockEventEdit,
} from "@/types/event.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { DateSelectArg, DatesSetArg, EventInput } from "@fullcalendar/core";
import enLocale from "@fullcalendar/core/locales/en-gb";
import ruLocale from "@fullcalendar/core/locales/ru";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import {
  Button,
  FontAwesomeIcon,
  Page,
  Stack,
  toCssColorValue,
  UIKitColors,
  useModals,
} from "@skailer-com/uikit";
import { addDays, addMinutes, format, getYear, parseISO, roundToNearestMinutes } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export function Calendar() {
  const [filter, setFilter] = useState<ApiEventFilter>({
    date_start: undefined,
    date_end: undefined,
  });
  const [currentTitle, setCurrentTitle] = useState<string>("");
  const calendarRef = useRef<FullCalendar>(null);
  const { t } = useTranslation();
  const modalsService = useModals();
  const { isFeatureEnabled, canIEdit, canViewField } = usePermissions();
  const { data: employee } = useEmployeeMe();
  const timezone =
    employee?.timezone ||
    employee?.organization.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const canViewTimeOffRequestsAndHolidays =
    isFeatureEnabled(ApiFeatureType.time_off) &&
    canIEdit(ApiPermission.manage_leave_policies) &&
    canViewField(employee, EmployeeFieldPermission.employee_time_off);
  useTitle(t("common:calendar"));

  const { data: apiEvents } = useEvents(
    filter,
    isFeatureEnabled(ApiFeatureType.core_company_calendar)
  );

  const calendarYear = useMemo(() => {
    return filter.date_start ? getYear(filter.date_start) : new Date().getFullYear();
  }, [filter.date_start]);

  const { data: timeOffCalendar } = useEmployeeTimeOffCalendar(
    employee?.id ?? 0,
    calendarYear,
    canViewTimeOffRequestsAndHolidays
  );

  const timeOffEvents = useMemo((): EventInput[] => {
    if (!timeOffCalendar) return [];

    const events: EventInput[] = [];

    for (const holiday of timeOffCalendar.calendar_holidays) {
      events.push({
        id: `holiday-${holiday.id}`,
        title: holiday.title,
        start: format(holiday.date, "yyyy-MM-dd"),
        end: format(addDays(holiday.date, 1), "yyyy-MM-dd"),
        allDay: true,
        display: "transparent",
        backgroundColor: "rgba(253, 81, 81, 0.15)",
        extendedProps: {
          eventType: CalendarEventType.holiday,
          emoji: ":tada:",
          data: holiday,
        },
      });
    }

    for (const request of timeOffCalendar.requests) {
      const categoryColor = request.time_off_data.category.color;

      events.push({
        id: `timeoff-${request.id}`,
        title: request.time_off_data.category.title,
        start: format(request.time_off_data.date_from, "yyyy-MM-dd"),
        end: format(addDays(request.time_off_data.date_to, 1), "yyyy-MM-dd"),
        allDay: true,
        backgroundColor: toCssColorValue(categoryColor, UIKitColors.dark),
        borderColor: "transparent",
        extendedProps: {
          eventType: CalendarEventType.time_off,
          emoji: request.time_off_data.category.emoji,
          data: request,
        },
      });
    }

    return events;
  }, [timeOffCalendar]);

  const fullCalendarLocale = useMemo(() => {
    if (!employee) return enLocale;
    if (employee.language == "ru") return ruLocale;

    return enLocale;
  }, [employee]);

  const transformEventForCalendar = (event: ApiEvent): EventInput => {
    return {
      id: `event-${event.id}`,
      title: event.title,
      start: event.is_all_day ? format(event.datetime_start, "yyyy-MM-dd") : event.datetime_start,
      end: event.is_all_day ? format(event.datetime_end, "yyyy-MM-dd") : event.datetime_end,
      allDay: event.is_all_day,
      extendedProps: {
        eventType: CalendarEventType.event,
        data: event,
      },
    };
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    const calendarApi = arg.view.calendar;

    setFilter({
      // The API accepts inclusive UTC dates, whereas FullCalendar returns zoned instants.
      date_start: parseISO(arg.start.toISOString().slice(0, 10)),
      date_end: parseISO(new Date(arg.end.getTime() - 1).toISOString().slice(0, 10)),
    });

    setCurrentTitle(calendarApi.view.title);
  };

  const handlePrevClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.prev();
  };

  const handleNextClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.next();
  };

  const handleTodayClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.today();
  };

  const handleAddEvent = (dateArgs: DateClickArg | undefined = undefined) => {
    if (employee) {
      const eventToEdit: ApiEventEdit = getMockEventEdit(employee);
      eventToEdit.timezone = timezone;

      if (dateArgs?.date) {
        const roundedTime = roundToNearestMinutes(toZonedTime(dateArgs.date, timezone), {
          nearestTo: 30,
        });
        eventToEdit.datetime_start = dateArgs.allDay
          ? roundedTime
          : fromZonedTime(roundedTime, timezone);
        eventToEdit.datetime_end = addMinutes(eventToEdit.datetime_start, 30);
        eventToEdit.is_all_day = dateArgs.allDay || false;
      }

      modalsService.openModal(EventEditModal, {
        event: eventToEdit,
      });
    }
  };

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    if (!employee) return;

    const eventToEdit: ApiEventEdit = getMockEventEdit(employee);
    eventToEdit.timezone = timezone;
    eventToEdit.datetime_start = selectInfo.allDay
      ? toZonedTime(selectInfo.start, timezone)
      : selectInfo.start;
    eventToEdit.datetime_end = selectInfo.allDay
      ? toZonedTime(selectInfo.end, timezone)
      : selectInfo.end;
    eventToEdit.is_all_day = selectInfo.allDay || false;

    const eventToShowWhileEditing: ApiEvent = {
      ...eventToEdit,
      id: 0,
      organizer: eventToEdit.attendees[0],
      interview: undefined,
    };
    selectInfo.view.calendar.unselect();

    apiEvents?.push(eventToShowWhileEditing);
    if (selectInfo.view.type === "timeGridWeek" || selectInfo.view.type === "timeGridDay") {
      modalsService.openModal(EventEditModal, {
        event: eventToEdit,
        onClose: () => {
          if (apiEvents && apiEvents.length > 0 && apiEvents[apiEvents.length - 1].id === 0) {
            apiEvents.pop();
          }
        },
      });
    }
  };

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.events.calendarViewed, {});
  });

  return (
    <Page className="h-full overflow-y-auto">
      <Page.Header
        title={currentTitle}
        titleSlot={
          <Stack gap={"xs"} direction={"horizontal"} className={"ml-2"}>
            <Button
              variant={"secondary"}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-chevron-left" />}
              onClick={handlePrevClick}
            />
            <Button variant={"secondary"} onClick={handleTodayClick}>
              {t("common:today")}
            </Button>
            <Button
              variant={"secondary"}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
              onClick={handleNextClick}
            />
          </Stack>
        }
      >
        <Button
          leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          onClick={() => {
            handleAddEvent();
          }}
        >
          {t("calendar:event_create_title")}
        </Button>
      </Page.Header>
      <Page.Content className={"h-full overflow-y-auto"}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, calendarTimeZonePlugin]}
          timeZone={timezone}
          locale={fullCalendarLocale}
          initialView="timeGridWeek"
          datesSet={handleDatesSet}
          select={handleDateSelect}
          dayCellClassNames={(arg) => {
            const day = toZonedTime(arg.date, timezone).getDay();
            return day === 0 || day === 6 ? "bg-dark/5" : "";
          }}
          views={{
            timeGridDay: {
              slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false },
            },
            timeGridWeek: {
              slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false },
            },
          }}
          dayHeaderFormat={{
            weekday: "short",
            day: "numeric",
          }}
          height={"100%"}
          allDaySlot={true}
          headerToolbar={false}
          events={[
            ...(apiEvents?.map((event) => transformEventForCalendar(event)) ?? []),
            ...timeOffEvents,
          ]}
          nowIndicator={true}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          eventContent={(eventInfo) => (
            <EventInCalendar
              eventInfo={eventInfo}
              viewAsEmployeeId={employee?.id}
              timezone={timezone}
            />
          )}
          selectAllow={(selectInfo) => {
            const start = selectInfo.start;
            const end = selectInfo.end;
            return (
              format(toZonedTime(start, timezone), "yyyy-MM-dd") ===
              format(toZonedTime(end, timezone), "yyyy-MM-dd")
            );
          }}
        />
      </Page.Content>
    </Page>
  );
}
